import React, { useContext } from "react"
import { defineMessage, FormattedMessage, useIntl } from "react-intl"
import { Link, Typography } from "@carrotfertility/carotene-core"
import { Link as RouterLink } from "react-router-dom"
import { SelectStep } from "../variants/SelectStep"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { CarrotCardCostSharePreAuthContext } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { useConvertTranslatedLabel } from "#/services/common-forms/useConvertTranslatedLabel"
import { FormSelectOption } from "@carrotfertility/carotene-core-x"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import { JourneySubTypeValues } from "#/components/carrot-card-cost-share/steps/shared/stepOptionValues"
import { Paths } from "#/utils/Paths"
import { useFlags } from "launchdarkly-react-client-sdk"

const baseWhatKindFertilityCareOptions = [
  {
    label: defineMessage({ defaultMessage: "Fertility testing" }),
    value: "FERTILITY_TESTING"
  },
  {
    label: defineMessage({ defaultMessage: "Intrauterine insemination (IUI)" }),
    value: JourneyTypes.IUI
  },
  {
    label: defineMessage({ defaultMessage: "In vitro fertilization (IVF)" }),
    value: JourneyTypes.IVF
  },
  {
    label: defineMessage({ defaultMessage: "Other fertility treatments" }),
    value: "OTHER_FERTILITY_TREATMENTS"
  }
]

const pelvicFloorTherapyOption = {
  label: defineMessage({ defaultMessage: "Pelvic floor physical therapy" }),
  value: JourneySubTypeValues.PELVIC_FLOOR_THERAPY
}

export function WhatKindFertilityCareStep(): JSX.Element {
  const intl = useIntl()
  const { send } = useStateMachine()
  const {
    dispatchFormState,
    formState: { whatKindFertilityCare },
    serverState: { hasCompletedIvfRequirements }
  } = useContext(CarrotCardCostSharePreAuthContext)
  const { pelvicFloorTherapyReimbursementLaunched } = useFlags()

  const whatKindFertilityCareOptions = pelvicFloorTherapyReimbursementLaunched
    ? [
        ...baseWhatKindFertilityCareOptions.slice(0, 3),
        pelvicFloorTherapyOption,
        ...baseWhatKindFertilityCareOptions.slice(3)
      ]
    : baseWhatKindFertilityCareOptions

  const formattedOptions = useConvertTranslatedLabel(whatKindFertilityCareOptions) as FormSelectOption[]

  const onSubmit = (formValues: { [key: string]: string }): void => {
    dispatchFormState({
      type: "UPDATE_FIELD",
      field: "whatKindFertilityCare",
      value: formValues.whatKindFertilityCare
    })

    if (formValues.whatKindFertilityCare === JourneySubTypeValues.PELVIC_FLOOR_THERAPY) {
      send(JourneySubTypeValues.PELVIC_FLOOR_THERAPY)
      return
    }

    if (formValues.whatKindFertilityCare === JourneyTypes.IVF) {
      if (!hasCompletedIvfRequirements) {
        send("NEEDS_IVF_REQUIREMENTS")
        return
      } else {
        send(formValues.whatKindFertilityCare)
        return
      }
    }
    send("")
  }

  return (
    <SelectStep
      title={intl.formatMessage({ defaultMessage: "What kind of fertility care is this related to?" })}
      description={
        <Typography sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })}>
          <FormattedMessage
            defaultMessage="If you're not sure or don't see something listed, <link>contact us</link>."
            values={{
              link: (linkContent) => (
                <Link
                  component={RouterLink}
                  to={Paths.CARE_TEAM}
                  color="inherit"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {linkContent}
                </Link>
              )
            }}
          />
        </Typography>
      }
      selectName="whatKindFertilityCare"
      options={formattedOptions}
      onSubmit={onSubmit}
      defaultValue={whatKindFertilityCare}
    />
  )
}
